import { useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { MdCall, MdEmail, MdOpenInNew } from "react-icons/md";
import { FiMessageSquare, FiCalendar } from "react-icons/fi";
import { API_BASE_URL } from "utils/api";

const getStatusBadgeColor = (status) => {
  const colors = {
    new: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    contacted: "bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-300",
    interested: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
    qualified: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    closed: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  };
  return colors[status?.toLowerCase()] || "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300";
};

const getPriorityBadgeColor = (priority) => {
  const colors = {
    high: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    medium: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    low: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300",
  };
  return colors[priority?.toLowerCase()] || "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300";
};

const ProfileCard = ({ item, type = "lead" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const notesCount = item?.notes?.length || 0;
  const schedulesCount = item?.schedules?.length || 0;

  const handleViewProfile = async () => {
    let isClientType = false;
    let recordId = item._id || item.id;
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      const normalizedType = String(type || "").toLowerCase();
      isClientType = normalizedType === "client" || normalizedType === "clients";
      recordId = item._id || item.id;
      const endpoint = isClientType
        ? `${API_BASE_URL}/clients/${recordId}`
        : `${API_BASE_URL}/leads/${recordId}`;

      const response = await fetch(endpoint, {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch profile data");
      }

      const data = await response.json();
      const profileData = data?.data || data;

      if (isClientType) {
        navigate(`/admin/client-management/client-profile/${recordId}`, { state: { client: profileData, from: location.pathname } });
      } else {
        navigate(`/admin/lead-management/lead-profile/${recordId}`, { state: { lead: profileData, from: location.pathname } });
      }
    } catch (error) {
      console.error("Error fetching profile data:", error);
      // Fallback to basic navigation if API call fails
      if (isClientType) {
        navigate(`/admin/client-management/client-profile/${recordId}`, { state: { client: item, from: location.pathname } });
      } else {
        navigate(`/admin/lead-management/lead-profile/${recordId}`, { state: { lead: item, from: location.pathname } });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="group relative bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-navy-700 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer overflow-hidden">
      {/* Card Content */}
      <div className="p-3 flex flex-col h-full">
        
        {/* Top Section: Name and Location */}
        <div className="mb-2.5">
          <h3 className="text-sm font-semibold text-navy-700 dark:text-white truncate">
            {item?.name || "Unknown"}
          </h3>
          {(item?.district || item?.state) && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {item?.district}
              {item?.state ? `, ${item?.state}` : ""}
            </p>
          )}
        </div>

        {/* Status and Priority Badges */}
        <div className="flex gap-1.5 mb-2.5 flex-wrap">
          {item?.status && (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(item?.status)}`}>
              {item?.status?.charAt(0).toUpperCase() + item?.status?.slice(1)}
            </span>
          )}
          {item?.priority && (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityBadgeColor(item?.priority)}`}>
              {item?.priority?.charAt(0).toUpperCase() + item?.priority?.slice(1)}
            </span>
          )}
        </div>

        {/* Contact Information */}
        <div className="space-y-1.5 mb-2.5 flex-1">
          {item?.email && (
            <div className="flex items-center gap-1.5 min-h-5">
              <MdEmail className="text-gray-400 dark:text-gray-600 flex-shrink-0 text-xs" />
              <a
                href={`mailto:${item?.email}`}
                className="text-xs text-gray-600 dark:text-gray-400 hover:text-brand-500 dark:hover:text-brand-400 truncate transition-colors"
                title={item?.email}
              >
                {item?.email}
              </a>
            </div>
          )}

          {item?.phone && (
            <div className="flex items-center gap-1.5 min-h-5">
              <MdCall className="text-gray-400 dark:text-gray-600 flex-shrink-0 text-xs" />
              <a 
                href={`tel:${item?.phone}`} 
                className="text-xs text-gray-600 dark:text-gray-400 hover:text-brand-500 dark:hover:text-brand-400 transition-colors"
              >
                {item?.phone}
              </a>
            </div>
          )}
        </div>

        {/* Additional Badges */}
        {(item?.source || item?.currency) && (
          <div className="flex gap-1.5 mb-2.5 flex-wrap">
            {item?.source && (
              <span className="px-2 py-0.5 bg-gray-100 dark:bg-navy-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">
                {item.source}
              </span>
            )}
            {item?.currency && (
              <span className="px-2 py-0.5 bg-gray-100 dark:bg-navy-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">
                {item.currency}
              </span>
            )}
          </div>
        )}

        {/* Metadata Row */}
        <div className="flex items-center justify-between gap-2 mb-2.5 pt-2 border-t border-gray-100 dark:border-navy-700 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-1.5">
            {notesCount > 0 && (
              <div className="flex items-center gap-0.5">
                <FiMessageSquare className="text-gray-400 dark:text-gray-600 text-xs" />
                <span>{notesCount}</span>
              </div>
            )}
            {schedulesCount > 0 && (
              <div className="flex items-center gap-0.5">
                <FiCalendar className="text-gray-400 dark:text-gray-600 text-xs" />
                <span>{schedulesCount}</span>
              </div>
            )}
          </div>
          <span className="text-xs">
            {new Date(item?.createdAt).toLocaleDateString()}
          </span>
        </div>

        {/* View Button */}
        <button 
          onClick={handleViewProfile}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-1.5 px-2.5 py-1.5 bg-brand-500 hover:bg-brand-600 active:bg-brand-700 text-white text-xs font-medium rounded-lg transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <MdOpenInNew className="text-sm" />
          {isLoading ? "Loading..." : "View"}
        </button>
      </div>
    </div>
  );
};

export default ProfileCard;
